import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { buildQuery, useApi } from './client'

export type RecurringTemplate = {
  id: string
  organizationId: string
  name: string
  rule: string
  type: 'income' | 'expense' | 'transfer'
  accountId: string
  counterpartyId: string | null
  categoryId: string
  amount: number
  currency: 'UAH' | 'USD' | 'EUR'
  description: string | null
  nextRunDate: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export type RecurringFilter = {
  isActive?: boolean
  type?: 'income' | 'expense' | 'transfer'
}

export type RecurringCreateInput = Omit<
  RecurringTemplate,
  'id' | 'nextRunDate' | 'isActive' | 'createdAt' | 'updatedAt' | 'counterpartyId' | 'description'
> & {
  counterpartyId?: string
  description?: string
  isActive?: boolean
}

export type RecurringPatchInput = Partial<
  Omit<RecurringTemplate, 'id' | 'organizationId' | 'createdAt' | 'updatedAt'>
>

const ROOT = '/recurring-templates'
const KEY = ['recurring'] as const

type ListResp = { items: RecurringTemplate[] }

export function useRecurring(filter: RecurringFilter = {}) {
  const api = useApi()
  return useQuery({
    queryKey: [...KEY, 'list', filter],
    queryFn: () =>
      api<ListResp>(
        `${ROOT}${buildQuery({
          is_active: filter.isActive != null ? String(filter.isActive) : undefined,
          type: filter.type,
        })}`,
      ),
  })
}

export function useCreateRecurring() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: RecurringCreateInput) =>
      api<RecurringTemplate>(ROOT, { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useUpdateRecurring() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & RecurringPatchInput) =>
      api<RecurringTemplate>(`${ROOT}/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useDeleteRecurring() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<void>(`${ROOT}/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useMaterializeNow() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api<{ created: number }>(`${ROOT}/${id}/materialize-now`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: ['planned'] })
    },
  })
}
